import { call, delay, put, race, takeLatest } from 'redux-saga/effects';
import { authAPI } from '../../api/api';
import { normalizeUserProfile } from '../../utils/commerce';
import { authActions } from './actions';
import {
  AUTH_BOOTSTRAP_SESSION_REQUEST,
  AUTH_CHANGE_PASSWORD_REQUEST,
  AUTH_FETCH_ME_REQUEST,
  AUTH_LOGIN_REQUEST,
  AUTH_LOGOUT,
  AUTH_REGISTER_REQUEST,
  AUTH_SETUP_SUPER_ADMIN_REQUEST,
  AUTH_UPDATE_ME_REQUEST,
} from './actionTypes';

const TOKEN_KEY = 'sedyam_token';
const PROFILE_KEY = 'sedyam_profile';
const CHECKOUT_STATE_KEY = 'sedyam_checkout_state';
const SAVED_PAYMENT_INSTRUMENTS_KEY = 'sedyam_saved_payment_instruments';
const BOOTSTRAP_SESSION_MAX_WAIT_MS = 8000;

const persistProfile = (profile) => {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(normalizeUserProfile(profile)));
};

const persistAuth = (token, profile) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (profile) persistProfile(profile);
};

const clearAuthStorage = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PROFILE_KEY);
  localStorage.removeItem(SAVED_PAYMENT_INSTRUMENTS_KEY);
  sessionStorage.removeItem(CHECKOUT_STATE_KEY);
};

const resolveAction = (action, payload) => {
  if (typeof action?.meta?.resolve === 'function') action.meta.resolve(payload);
};

const rejectAction = (action, error) => {
  if (typeof action?.meta?.reject === 'function') action.meta.reject(error);
};

function* bootstrapSessionSaga(action) {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    yield put(authActions.bootstrapSessionSuccess({ profile: null }));
    resolveAction(action, { data: null });
    return;
  }

  try {
    const { res, timedOut } = yield race({
      res: call(authAPI.getMe),
      timedOut: delay(BOOTSTRAP_SESSION_MAX_WAIT_MS),
    });

    if (timedOut || !res) {
      yield put(
        authActions.bootstrapSessionFailure({
          message: 'Session refresh timed out. Please continue and login again if needed.',
        })
      );
      resolveAction(action, { data: null, staleSession: true, timedOut: true });
      return;
    }

    const profile = normalizeUserProfile(res.data);
    persistProfile(profile);
    yield put(authActions.bootstrapSessionSuccess({ profile }));
    resolveAction(action, res);
  } catch (error) {
    const status = Number(error?.status || 0);
    const isAuthFailure = status === 401 || status === 403;

    if (isAuthFailure) {
      clearAuthStorage();
      yield put(authActions.logout());
      yield put(authActions.bootstrapSessionFailure({ message: error?.message || 'Session expired.' }));
      rejectAction(action, error);
      return;
    }

    yield put(
      authActions.bootstrapSessionFailure({
        message: error?.message || 'Unable to refresh session right now.',
      })
    );
    resolveAction(action, { data: null, staleSession: true });
  }
}

function* loginSaga(action) {
  try {
    const res = yield call(authAPI.login, action.payload);
    const profile = normalizeUserProfile(res.data);
    persistAuth(res.token, profile);
    yield put(authActions.loginSuccess({ profile }));
    resolveAction(action, res);
  } catch (error) {
    yield put(authActions.loginFailure({ message: error?.message || 'Login failed.' }));
    rejectAction(action, error);
  }
}

function* registerSaga(action) {
  try {
    const res = yield call(authAPI.register, action.payload);
    const profile = normalizeUserProfile(res.data);
    persistAuth(res.token, profile);
    yield put(authActions.registerSuccess({ profile }));
    resolveAction(action, res);
  } catch (error) {
    yield put(authActions.registerFailure({ message: error?.message || 'Registration failed.' }));
    rejectAction(action, error);
  }
}

function* fetchMeSaga(action) {
  try {
    const res = yield call(authAPI.getMe);
    const profile = normalizeUserProfile(res.data);
    persistProfile(profile);
    yield put(authActions.fetchMeSuccess({ profile }));
    resolveAction(action, res);
  } catch (error) {
    yield put(authActions.fetchMeFailure({ message: error?.message || 'Failed to load profile.' }));
    rejectAction(action, error);
  }
}

function* updateMeSaga(action) {
  try {
    const res = yield call(authAPI.updateMe, action.payload);
    const profile = normalizeUserProfile(res.data);
    persistProfile(profile);
    yield put(authActions.updateMeSuccess({ profile }));
    resolveAction(action, res);
  } catch (error) {
    yield put(authActions.updateMeFailure({ message: error?.message || 'Failed to update profile.' }));
    rejectAction(action, error);
  }
}

function* changePasswordSaga(action) {
  try {
    const res = yield call(authAPI.changePassword, action.payload);
    yield put(authActions.changePasswordSuccess({ message: res?.message || 'Password changed successfully.' }));
    resolveAction(action, res);
  } catch (error) {
    yield put(authActions.changePasswordFailure({ message: error?.message || 'Failed to change password.' }));
    rejectAction(action, error);
  }
}

function* setupSuperAdminSaga(action) {
  try {
    const res = yield call(authAPI.setupSuperAdmin, action.payload);
    yield put(authActions.setupSuperAdminSuccess({ message: res?.message || 'Super admin created.' }));
    resolveAction(action, res);
  } catch (error) {
    yield put(authActions.setupSuperAdminFailure({ message: error?.message || 'Failed to create super admin.' }));
    rejectAction(action, error);
  }
}

function* logoutSaga() {
  yield call(clearAuthStorage);
}

export default function* authSaga() {
  yield takeLatest(AUTH_BOOTSTRAP_SESSION_REQUEST, bootstrapSessionSaga);
  yield takeLatest(AUTH_LOGIN_REQUEST, loginSaga);
  yield takeLatest(AUTH_REGISTER_REQUEST, registerSaga);
  yield takeLatest(AUTH_FETCH_ME_REQUEST, fetchMeSaga);
  yield takeLatest(AUTH_UPDATE_ME_REQUEST, updateMeSaga);
  yield takeLatest(AUTH_CHANGE_PASSWORD_REQUEST, changePasswordSaga);
  yield takeLatest(AUTH_SETUP_SUPER_ADMIN_REQUEST, setupSuperAdminSaga);
  yield takeLatest(AUTH_LOGOUT, logoutSaga);
}
